import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { EllipsisVertical, Plus } from 'lucide-react';
import { useTags, type Tag } from '@/hooks/useTags';
import { findTagByName, normalizeTagName } from '@/lib/tagName';
import { SettingsCard } from './SettingsCard';
import { Checkbox } from '../ui/checkbox';
import ScrollArea from '../ui/shared/ScrollArea';
import { useTranslation } from "react-i18next";


const TagsTab: React.FC = () => {
  const { t } = useTranslation("settings");
  const { tags, loading, createTag, updateTag, deleteTag } = useTags();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTag, setEditingTag] = useState<Tag | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    is_active: true
  });
  const [formError, setFormError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const name = normalizeTagName(formData.name);
    if (!name) return;

    // O nome é comparado sem caixa: "CLAUDE" e "claude" são a mesma tag, e deixar
    // as duas passarem é o que fazia a lista aparecer duplicada.
    const clash = findTagByName(tags, name);
    if (clash && clash.id !== editingTag?.id) {
      setFormError(t('tags.duplicate'));
      return;
    }

    setFormError(null);

    if (editingTag) {
      const success = await updateTag(editingTag.id, { ...formData, name, color: '#3B82F6' });
      if (success) {
        setDialogOpen(false);
        resetForm();
      }
    } else {
      const created = await createTag({ ...formData, name, color: '#3B82F6' });
      if (created) {
        setDialogOpen(false);
        resetForm();
      }
    }
  };

  const handleEdit = (tag: Tag) => {
    setEditingTag(tag);
    setFormData({
      name: tag.name,
      is_active: tag.is_active
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (confirm(t("tags.confirmDelete"))) {
      await deleteTag(id);
    }
  };

  const resetForm = () => {
    setEditingTag(null);
    setFormData({
      name: '',
      is_active: true
    });
    setFormError(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold text-[#0F172A] dark:text-[#FAFAFA]">{t("tags.title")}</h2>
          <p className="text-sm text-[#64748B] dark:text-[#71717A]">{t("tags.subtitle")}</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="rounded-xl bg-[#2563EB] text-white shadow-sm hover:bg-[#1D4ED8] transition-colors duration-200 dark:bg-[#2563EB] dark:hover:bg-[#1D4ED8]">
              <Plus className="w-4 h-4 mr-2" />
              {t("tags.new")}
            </Button>
          </DialogTrigger>
          <DialogContent className="flex w-full p-0 rounded-2xl bg-white border border-[#E5E7EB] text-[#0F172A] dark:bg-[#27272A] dark:border-[#3F3F46] dark:text-[#FAFAFA] flex-col max-w-[500px]">
            <ScrollArea classNames={{ root: 'max-h-[90dvh] p-5 overflow-auto' }}>
              <DialogHeader>
                <DialogTitle className="text-[#0F172A] dark:text-[#FAFAFA]">
                  {editingTag ? 'Editar Tag' : 'Nova Tag'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="w-full mt-4 gap-4 flex flex-col">
                <div className="flex flex-col w-full items-start gap-3">
                  <Label htmlFor="name">{t("tags.tagName")}</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => { setFormData({ ...formData, name: e.target.value }); setFormError(null); }}
                    className="h-11 min-h-[36px] rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A]"
                    required
                  />
                  {formError && (
                    <p className="text-xs text-[#DC2626] dark:text-[#F87171]">{formError}</p>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: !!checked })}
                  />
                  <Label htmlFor="is_active" className="cursor-pointer">{t("tags.activeTag")}</Label>
                </div>

                <div className="flex gap-2 justify-end mt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    {t("common.cancel")}
                  </Button>
                  <Button type="submit" className="rounded-xl bg-[#2563EB] text-white shadow-sm hover:bg-[#1D4ED8] transition-colors duration-200 dark:bg-[#2563EB] dark:hover:bg-[#1D4ED8]">
                    {editingTag ? 'Salvar' : 'Criar'}
                  </Button>
                </div>
              </form>
            </ScrollArea>
          </DialogContent>
        </Dialog>
      </div>

      <SettingsCard>
        {loading ? (
          <div className="p-8 text-center text-[#64748B] dark:text-[#71717A]">{t("common.loading")}</div>
        ) : tags.length === 0 ? (
          <div className="p-8 text-center text-[#64748B] dark:text-[#71717A]">{t("tags.empty")}</div>
        ) : (
          <div className="divide-y divide-[#E5E7EB] dark:divide-[#3F3F464D]">
            {tags.map((tag) => (
              <div key={tag.id} className="p-4 flex items-center justify-between hover:bg-[#F8FAFC] transition-colors duration-200 dark:hover:bg-[#3F3F46]/50">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-[#0F172A] dark:text-[#FAFAFA]">{tag.name}</span>
                  {!tag.is_active && (
                    <span className="text-xs px-2 py-1 rounded-full bg-[#FEF2F2] text-[#DC2626] dark:bg-red-500/15 dark:text-[#F87171]">
                      {t("common.inactiveF")}
                    </span>
                  )}
                </div>
                <Popover>
                  <PopoverTrigger asChild>
                    <button className="flex justify-center items-center rounded-lg border border-[#E5E7EB] bg-white w-[34px] h-[34px] text-[#64748B] hover:bg-[#F1F5F9] hover:text-[#2563EB] transition-colors duration-200 dark:border-[#3F3F46] dark:bg-transparent dark:text-[#A1A1AA] dark:hover:bg-[#27272A] dark:hover:text-[#60A5FA]" aria-label={t("tags.tagActions")}>
                      <EllipsisVertical size={18} className="text-current" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="!bg-white p-1 w-[95px] flex flex-col gap-1 border border-[#E5E7EB] rounded-xl shadow-md dark:!bg-[#27272A] dark:border-[#3F3F46] dark:shadow-none">
                    <button
                      onClick={() => handleEdit(tag)}
                      className="w-full flex justify-center rounded-lg items-center bg-transparent hover:bg-[#F1F5F9] min-h-[29px] text-[#0F172A] transition-colors duration-200 dark:hover:bg-[#3F3F46] dark:text-[#FAFAFA]"
                    >
                      {t("common.edit")}
                    </button>
                    <button
                      onClick={() => handleDelete(tag.id)}
                      className="w-full flex justify-center rounded-lg items-center bg-transparent hover:bg-[#FEF2F2] min-h-[29px] text-[#DC2626] transition-colors duration-200 dark:hover:bg-red-500/10 dark:text-[#F87171]"
                    >
                      {t("common.delete")}
                    </button>
                  </PopoverContent>
                </Popover>
              </div>
            ))}
          </div>
        )}
      </SettingsCard>
    </div>
  );
};

export default TagsTab;
